import { memo, useCallback, useEffect, useState } from "react";


import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { VehicleTireSetSearchFilterProps } from "./types";

import { useTranslate } from "helpers";
import { FleetTireManufacturer, FleetTireSetSearchFormValues, TireHeight, TireRadius, TireType, TireWidth } from "services/api/fleet/tires/types";
import { initialFleetTireSetSearchForm } from "services/api/fleet/tires/consts";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { getTireHeightsAPI, getTireRadiusAPI, getTireTypesAPI, getTireWidthsAPI } from "services/api/fleet/tires/api";
import { getTireManufacturersAPI } from "services/api/calculations/tire/api";
import { initialTireManufacturerDataWithPagination } from "ui/modules/calculation/pages/CalculationSettingPage/components/TireEditCard/consts";
import { Errors } from "services/forms/validation/types";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { useHandleDropdownChange } from "services/forms/hooks";
import TireSetSearchFiltersModal from "ui/modules/fleet/tires/pages/TireSetSearchPage/components/TireSetSearchFilters/TireSetSearchFiltersModal";
import { Chip } from "ui/components/Chip";

const VehicleTireSetSearchFilters: React.FC<VehicleTireSetSearchFilterProps> = (
  props
) => {
  const { onSearchChange } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetTireSetSearchFormValues>(
    initialFleetTireSetSearchForm
  );

  const [tireTypes, setTireTypes] = useState<DataWithPagination<TireType>>(
    initialDataWithPagination
  );
  const [tireWidths, setTireWidths] = useState<TireWidth[]>([]);
  const [tireHeights, setTireHeights] = useState<TireHeight[]>([]);
  const [tireRadius, setTireRadius] = useState<TireRadius[]>([]);
  const [tireManufacturers, setTireManufacturers] = useState<
    DataWithPagination<FleetTireManufacturer>
  >(initialTireManufacturerDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    fetchTireTypes(tireTypes.pagination);
    fetchTireWidth();
    fetchTireHeight();
    fetchTireradius();
    fetchTireManufacturers();
  }, []);


  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const updatedFormValues = { ...formValues, search: e.target.value };
    setFormValues(updatedFormValues);
    onSearchChange(updatedFormValues);
  };



  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetTireSetSearchForm);
  }, []);

  const handleAdditionalFilterClick = useCallback(
    (formValues: FleetTireSetSearchFormValues) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...formValues,
      }));
      onSearchChange(formValues);
      setShowModal(false);
    },
    [setFormValues, setShowModal]
  );

  const handleRemoveFilterClick = useCallback(
    (id: string, name: string, text: string) => {
      setFormValues((prevValues) => {
        return {
          ...prevValues,
          [name]: null,
          advancedSearch: prevValues.advancedSearch.filter(
            (item) => item.name !== name
          ),
        };
      });
      onSearchChange(formValues);
    },
    [setFormValues]
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  const fetchTireTypes = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getTireTypesAPI(pagination);

      setTireTypes(results);
    } catch {
      setTireTypes(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireWidth = async () => {
    setIsLoading(true);
    try {
      const results = await getTireWidthsAPI();

      setTireWidths(results);
    } catch {
      setTireWidths([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireHeight = async () => {
    setIsLoading(true);
    try {
      const results = await getTireHeightsAPI();

      setTireHeights(results);
    } catch {
      setTireHeights([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireradius = async () => {
    setIsLoading(true);
    try {
      const results = await getTireRadiusAPI();

      setTireRadius(results);
    } catch {
      setTireRadius([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireManufacturers = async () => {
    setIsLoading(true);

    let result;
    try {
      result = await getTireManufacturersAPI(tireManufacturers.pagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setTireManufacturers(result);

    setIsLoading(false);

    setErrors({});
  };

  const tireTypeItems: DropdownOption[] = tireTypes.data.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const widthItems: DropdownOption[] = tireWidths.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const heightItems: DropdownOption[] = tireHeights.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const radiusItems: DropdownOption[] = tireRadius.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const manufacturerItems: DropdownOption[] = tireManufacturers?.data.map(
    (i) => {
      return {
        value: i.id,
        text: i.name,
      };
    }
  );

  return (
    <>
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-yellow-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-yellow-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Ljetne gume
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Zimske gume
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-yellow-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Cjelogodišnje gume
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center ">
        <div className="flex flex-wrap gap-3">
          <div className="md:w-80">
            <TextField
              type="text"
              name="search"
              value={formValues.search}
              onChange={handleSearchChange}
              placeholder={t("admin.search.placeholder")}
              startIcon="../media/icons/search.svg"
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="width"
              placeholder={t("admin.tire.search.filter.width.placeholder")}
              options={widthItems}
              onChange={handleDropdownChange}
              value={formValues.width}
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="height"
              placeholder={t(
                "admin.tire.search.filter.height.placeholder"
              )}
              options={heightItems}
              onChange={handleDropdownChange}
              value={formValues.height}
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="radius"
              placeholder={t("admin.tire.search.filter.radius.placeholder")}
              options={radiusItems}
              onChange={handleDropdownChange}
              value={formValues.radius}
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              placeholder={t("admin.tire.search.filter.manufacturer.placeholder")}
              name="tireManufacturerId"
              options={manufacturerItems}
              onChange={handleDropdownChange}
              value={formValues.tireManufacturerId}
              direction="down"
              error={errors.tireManufacturerId}
              cssClassName="bg-white"

            />
          </div>
          <Button
            variant="outlined"
            startIcon="/media/icons/text-search.svg"
            onClick={() => setShowModal(true)}
          />
          <div className="mr-3 md:mr-6 lg:md-12 flex items-center">
            <span
              onClick={handleResetFormClick}
              className="text-xs font-bold cursor-pointer text-gray-700"
            >
              {t("admin.reset")}
            </span>
          </div>
        </div>
      </div>
      <div className="mb-3 flex gap-1">
        {formValues.advancedSearch &&
          formValues.advancedSearch.map((item, index: number) => {
            return (
              <Chip
                name={item.name}
                id={item.value}
                text={`${item.label ? `${item.label}:` : ""}${item.text}`}
                key={index}
                onClick={handleRemoveFilterClick}
              />
            );
          })}
      </div>
      <TireSetSearchFiltersModal
        show={showModal}
        onSuccess={handleAdditionalFilterClick}
        onClose={() => setShowModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
      />
      {/* <div className=" ">
        <div className="flex justify-between items-center ">
          <div className="flex">
            <div className="mr-3">
              <TextField
                type="text"
                name="search"
                value={formValues.search}
                onChange={handleSearchChange}
                placeholder={t("admin.search.placeholder")}
                startIcon="../media/icons/search.svg"
              />
            </div>

            <div className="mr-3 flex items-center">
              <span
                onClick={handleResetFormClick}
                className="text-xs font-bold cursor-pointer text-gray-700"
              >
                  {t("admin.reset")}
              </span>
            </div>
          </div>
          <div className=" py-3 flex justify-end ">
            <Button
              variant="primary"
              children={t("admin.vehicle.tire.set.search.add")}
              startIcon="/media/icons/plus.svg"
              onClick={onAddNew}
            />
          </div>
        </div>
      </div> */}
    </>
  );
};
export default memo(VehicleTireSetSearchFilters);
